import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { CalendarBlank, Upload, Plus, Trash, X, Info } from '@phosphor-icons/react';
import api from '../../services/api';

const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export default function CalendarioFestivitaSection({ anno }) {
  const [custom, setCustom] = useState([]);
  const [national, setNational] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ date: '', label: '' });

  const load = useCallback(async () => {
    if (!anno) return;
    setLoading(true);
    try {
      const res = await api.getAdminHolidays(anno);
      setCustom(res.data?.custom || []);
      setNational(res.data?.national || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento festività');
    } finally { setLoading(false); }
  }, [anno]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file, replace = false) => {
    if (!file) return;
    if (replace && !window.confirm(`Sostituire TUTTE le festività custom (PDF + manuali) dell'a.a. ${anno}/${anno + 1}?`)) return;
    setUploading(true);
    try {
      const res = await api.importAdminHolidaysPdf(anno, file, replace);
      const { extracted, inserted, skipped_existing } = res.data || {};
      toast.success(`PDF importato · ${inserted} nuove festività${skipped_existing ? `, ${skipped_existing} già presenti` : ''} (${extracted} rilevate)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore import PDF');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Rimuovere questa festività?')) return;
    try {
      await api.deleteAdminHoliday(id);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore rimozione');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.date || !addForm.label.trim()) { toast.error('Compila data ed etichetta'); return; }
    try {
      await api.createAdminHoliday({ anno, date: addForm.date, label: addForm.label.trim(), source: 'manual' });
      toast.success('Festività aggiunta');
      setAddForm({ date: '', label: '' });
      setAddOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiunta');
    }
  };

  // Raggruppa festività per mese (custom + national uniti)
  const grouped = useMemo(() => {
    const all = [
      ...custom.map(h => ({ ...h, isNational: false })),
      ...national.map(h => ({ ...h, isNational: true })),
    ].sort((a, b) => a.date.localeCompare(b.date));
    const byMonth = {};
    for (const h of all) {
      const [y, m] = h.date.split('-');
      const key = `${y}-${m}`;
      if (!byMonth[key]) byMonth[key] = { year: y, month: parseInt(m, 10), items: [] };
      byMonth[key].items.push(h);
    }
    return Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => ({ key: k, ...v }));
  }, [custom, national]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendario-festivita-section">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#2D2A4A] font-['Work_Sans']">Calendario Festività</h2>
          <p className="text-sm text-[#52525B] mt-1">
            Gestisci le festività dell&apos;anno accademico <span className="font-semibold">{anno}/{(anno || 0) + 1}</span>. Vengono applicate a <span className="font-semibold">tutti gli alunni</span> nella generazione automatica delle presenze.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm text-white h-9"
            data-testid="btn-add-holiday"
          >
            <Plus size={16} className="mr-1" weight="bold" />Aggiungi
          </Button>
          <label className={`cursor-pointer flex items-center gap-1.5 px-3 h-9 rounded-sm text-sm font-medium border transition-colors ${uploading ? 'bg-[#F4F4F5] text-[#A1A1AA] border-[#E4E4E7]' : 'bg-white hover:bg-[#EEF2FF] text-[#2D2A4A] border-[#2D2A4A]'}`}>
            <Upload size={16} weight="bold" />{uploading ? 'Import...' : 'Importa PDF Calendario'}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              disabled={uploading}
              onChange={(e) => { if (e.target.files?.[0]) { handleUpload(e.target.files[0], false); e.target.value = ''; } }}
              data-testid="input-import-pdf"
            />
          </label>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 p-3 bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm text-xs text-[#4338CA]">
        <Info size={16} weight="bold" className="mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold uppercase tracking-wider mb-1">Come funziona</div>
          <div>
            Carica il PDF del calendario scolastico una sola volta per anno. Le date verranno riconosciute automaticamente (Natale, Pasqua, patrono, ponti, Carnevale). Puoi aggiungere/rimuovere singole date manualmente.
            Quando genererai le presenze per qualsiasi alunno, tutte queste date saranno marcate come <span className="font-semibold">&quot;Festivo&quot;</span>.
            Le festività nazionali italiane (Capodanno, Epifania, 25 Aprile, ecc.) sono già incluse automaticamente e non modificabili.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#A1A1AA] italic">Caricamento...</div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E4E4E7] rounded-sm">
          <CalendarBlank size={48} className="mx-auto text-[#A1A1AA] mb-3" weight="thin" />
          <p className="text-sm text-[#52525B]">Nessuna festività configurata per questo anno.</p>
          <p className="text-xs text-[#A1A1AA] mt-1">Carica il PDF del calendario scolastico o aggiungi manualmente.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="holidays-grid">
          {grouped.map(({ key, year, month, items }) => (
            <div key={key} className="border border-[#E4E4E7] rounded-sm bg-white overflow-hidden" data-testid={`month-block-${key}`}>
              <div className="bg-[#2D2A4A] text-white px-3 py-2 text-sm font-semibold font-['Work_Sans'] flex items-center justify-between">
                <span>{MONTH_NAMES[month - 1]} <span className="text-white/60 font-normal font-['Work_Sans']">{year}</span></span>
                <span className="text-[10px] font-normal text-white/60 uppercase tracking-wider">{items.length} {items.length === 1 ? 'giorno' : 'giorni'}</span>
              </div>
              <div className="divide-y divide-[#F4F4F5]">
                {items.map(h => (
                  <div key={h.id} className="flex items-center gap-2 px-3 py-1.5 text-xs group hover:bg-[#FAFAFA]" data-testid={`holiday-item-${h.id}`}>
                    <div className="w-10 text-center">
                      <div className="text-[9px] uppercase text-[#A1A1AA]">
                        {new Date(h.date + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short' })}
                      </div>
                      <div className="font-['Work_Sans'] font-bold text-[#2D2A4A]">{h.date.split('-')[2]}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#2D2A4A] truncate" title={h.label}>{h.label}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        {h.isNational && <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA] bg-[#F4F4F5] px-1 rounded">Nazionale</span>}
                        {h.source === 'pdf_import' && <span className="text-[9px] uppercase tracking-wider text-teal-700 bg-teal-50 px-1 rounded">PDF</span>}
                        {h.source === 'manual' && <span className="text-[9px] uppercase tracking-wider text-indigo-700 bg-indigo-50 px-1 rounded">Manuale</span>}
                      </div>
                    </div>
                    {!h.isNational && (
                      <button
                        onClick={() => handleDelete(h.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 p-1 rounded transition-all"
                        title="Rimuovi"
                        data-testid={`btn-delete-holiday-${h.id}`}
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Aggiungi Festività</DialogTitle>
            <DialogDescription>
              Aggiungi manualmente un giorno festivo per l&apos;a.a. {anno}/{(anno || 0) + 1}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Data</Label>
              <Input
                type="date"
                value={addForm.date}
                onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                className="rounded-sm"
                data-testid="input-holiday-date"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Etichetta / Motivo</Label>
              <Input
                type="text"
                value={addForm.label}
                onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                placeholder="es. Ponte Immacolata"
                className="rounded-sm"
                data-testid="input-holiday-label"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="btn-add-holiday-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
